import { useCallback, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  aiApi,
  type DescriptionTone,
  type DescriptionLength,
} from '../services/aiApi';
import { useAIJobStatus } from './useAIJobStatus';
import { useDescriptionStream } from '@/lib/realtime/hooks/useDescriptionStream';
import { useDealershipSettings } from '@/modules/crm/hooks/useDealershipSettings';

/**
 * Description-generation AI hook for the wizard.
 *
 * Phase 39-04: extended with Centrifugo streaming. When the ai-engine is
 * live (flag-on path), tokens arrive via `stream.text` as they're produced;
 * the existing `jobStatus` polling remains the ground-truth fallback for
 * when the stream drops, Centrifugo is misconfigured, or the flag-off
 * legacy path is taken (sync return, no stream events).
 *
 * Phase 40-03 UX-04: tone + length vocabulary chips in AIDescriptionCard
 * thread the user's selection through the mutation body to the Laravel
 * description dispatcher, which forwards to the ai-engine description
 * agent's prompt. Defaults per CONTEXT D-15: `tone='professional'`,
 * `length='medium'`.
 *
 * Phase 40-03 UX-05: last-3 regenerate carousel. Each time the stream
 * emits `done`, we push a new entry to `history` and cap at 3 (FIFO). The
 * carousel UX in AIDescriptionCard lets the user Prev/Next through
 * recent generations and "Revert to saved" to clear the cache. Storage
 * key convention: `wizard:{boatId}:descriptions:recent3`.
 *
 * Contract (additive over Phase 29/39-04):
 *   - `trigger`    — mutation that kicks off the job, now accepts no
 *                    arg (reads tone/length from hook state)
 *   - `tone` / `setTone`     — UX-04 state
 *   - `length` / `setLength` — UX-04 state
 *   - `jobStatus`  — useAIJobStatus (stream-driven since 40-03)
 *   - `jobId`      — set after trigger success
 *   - `reset`      — clears jobId + resets stream state
 *   - `isActive`   — polling-derived liveness flag
 *   - `stream`     — live token stream state:
 *                    `{ status, text, tokenCount, error }`
 *   - `history`, `currentIndex`, `currentDescription`  — UX-05 carousel
 *   - `showPrev`, `showNext`, `revertToSaved`           — UX-05 nav/clear
 */

/**
 * One entry in the regenerate carousel. Persisted to localStorage so the
 * user can navigate previous generations after reloading the wizard (draft
 * still exists) — cache clears when the draft is discarded.
 */
export interface DescriptionCacheEntry {
  text: string;
  tone: DescriptionTone;
  length: DescriptionLength;
  generatedAt: string;
}

const MAX_HISTORY = 3; // CONTEXT D-16 — FIFO cap (scope-locked)

/** Storage key per boat. Matches existing wizard draft convention. */
function storageKeyFor(boatId: string | null | undefined): string | null {
  if (!boatId) return null;
  return `wizard:${boatId}:descriptions:recent3`;
}

function loadInitialHistory(
  boatId: string | null | undefined,
): DescriptionCacheEntry[] {
  const key = storageKeyFor(boatId);
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Shape guard — ignore entries that don't match the expected shape so
    // a stale pre-40 cache (or a corrupted entry) doesn't crash the hook.
    const clean = parsed.filter(
      (e): e is DescriptionCacheEntry =>
        e &&
        typeof e === 'object' &&
        typeof e.text === 'string' &&
        typeof e.tone === 'string' &&
        typeof e.length === 'string' &&
        typeof e.generatedAt === 'string',
    );
    return clean.slice(0, MAX_HISTORY);
  } catch {
    return [];
  }
}

export function useAIDescription(boatId: string) {
  const [jobId, setJobId] = useState<string | undefined>();
  const { data: settings } = useDealershipSettings();
  const tenantId = settings?.tenant_id;

  // Phase 40-03 UX-04 — tone + length state. Defaults per D-15.
  const [tone, setTone] = useState<DescriptionTone>('professional');
  const [length, setLength] = useState<DescriptionLength>('medium');

  // Phase 40-03 UX-05 — carousel state, seeded from localStorage.
  const [history, setHistory] = useState<DescriptionCacheEntry[]>(() =>
    loadInitialHistory(boatId || null),
  );
  const [currentIndex, setCurrentIndex] = useState(0);

  // When boatId changes (e.g. draft create lands after trigger), reload the
  // carousel from storage — this keeps the resumed-draft path in sync.
  useEffect(() => {
    setHistory(loadInitialHistory(boatId || null));
    setCurrentIndex(0);
  }, [boatId]);

  const trigger = useMutation({
    // Pass the current tone/length at mutate-call time so re-renders
    // between chip select and Generate click always use the latest values.
    mutationFn: () => aiApi.generateDescription(boatId, { tone, length }),
    onSuccess: (data) => {
      setJobId(data.job_id);
    },
  });

  const jobStatus = useAIJobStatus(jobId);
  const stream = useDescriptionStream(tenantId, jobId);

  // Safety net: if a new trigger starts (mutation firing) while the stream
  // is still showing stale `done` state from a prior run, reset the stream
  // view too. The jobId change in setJobId already triggers a reset inside
  // useDescriptionStream — but the mutation might fire before jobId lands,
  // so clear proactively.
  useEffect(() => {
    if (trigger.isPending) {
      stream.reset();
    }
    // We intentionally depend only on the pending flag. stream.reset is a
    // stable useCallback ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger.isPending]);

  // Phase 40-03 UX-05 — push a new carousel entry on `done`. We intentionally
  // exclude tone/length from deps and snapshot them at write time via the
  // ref dance below (they're captured in the mutation closure already —
  // stream.done is the terminal event for whichever generation produced
  // the text we're reading now).
  useEffect(() => {
    if (stream.status !== 'done' || !stream.text) return;
    const key = storageKeyFor(boatId || null);
    if (!key) return;

    const entry: DescriptionCacheEntry = {
      text: stream.text,
      tone,
      length,
      generatedAt: new Date().toISOString(),
    };

    setHistory((prev) => {
      // Dedupe against the most recent entry — if the user somehow
      // re-enters `done` with identical text (double-subscribe race on
      // reconnect), don't bloat the carousel.
      if (prev.length > 0 && prev[0].text === entry.text) return prev;
      const next = [entry, ...prev].slice(0, MAX_HISTORY); // FIFO cap (D-16)
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // Quota exceeded — silent fallback per 40-03 Pitfall A. The
        // in-memory history still shows the new entry for this session;
        // persistence simply doesn't stick.
      }
      return next;
    });
    setCurrentIndex(0); // newest = show it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream.status, stream.text, boatId]);

  // Carousel navigation. Index 0 = newest; higher = older (so "Prev" means
  // "go further back in time" — increment index — and "Next" means "come
  // forward toward the newest" — decrement). Disabled states in the UI
  // mirror the clamps below.
  const showPrev = useCallback(() => {
    setCurrentIndex((i) => Math.min(i + 1, Math.max(0, history.length - 1)));
  }, [history.length]);

  const showNext = useCallback(() => {
    setCurrentIndex((i) => Math.max(i - 1, 0));
  }, []);

  const revertToSaved = useCallback(() => {
    const key = storageKeyFor(boatId || null);
    if (key) {
      try {
        localStorage.removeItem(key);
      } catch {
        // Storage unavailable — clear in-memory anyway.
      }
    }
    setHistory([]);
    setCurrentIndex(0);
  }, [boatId]);

  // Unmount cleanup (D-16 / 40-RESEARCH §Q9): clear the cache ONLY when
  // boatId is null at unmount time (draft was discarded). If boatId is
  // present, leave the cache so the user can revisit the wizard and
  // continue browsing previous generations.
  useEffect(() => {
    return () => {
      if (!boatId) {
        const key = storageKeyFor(null);
        if (key) {
          try {
            localStorage.removeItem(key);
          } catch {
            // no-op
          }
        }
      }
    };
    // Intentionally empty deps — unmount-only cleanup. The `boatId`
    // closure captures the value AT unmount via the effect setup-time
    // evaluation; we want the latest so we re-run the cleanup on boatId
    // change (which also effectively means "old boatId is unmounting").
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = useCallback(() => {
    setJobId(undefined);
    stream.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The description shown to the user in the review pane. If the user is
  // cycling through the carousel, show the selected history entry;
  // otherwise fall through to the live stream text (current generation).
  const currentDescription = history[currentIndex]?.text ?? stream.text;

  return {
    trigger,
    jobStatus,
    jobId,
    reset,
    isActive:
      !!jobId &&
      jobStatus.data?.status !== 'completed' &&
      jobStatus.data?.status !== 'failed',
    stream,
    // UX-04
    tone,
    setTone,
    length,
    setLength,
    // UX-05
    history,
    currentIndex,
    currentDescription,
    showPrev,
    showNext,
    revertToSaved,
  };
}
